import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {DeleteCellComponent} from '@irb-common/components/grid/renderers/delete.cell.component';
import {EditorMmcModelService} from '@irb/irb-editors/editor-mmc-model/editor-mmc-model.service';
import {IrbAppEvents} from '@irb/app.events';
import {AppService} from '@irb/app.service';
import {CalltoactionService} from '@irb-common/components/calltoaction/calltoaction.service';
import {IDropdownItem} from '@irb-common/models/dropdown.model';
import {DropdownFilterRendererComponent} from '@irb-common/components/grid/renderers/dropdown-filter.cell.component';
import {NumberTextBoxEditorComponent} from '@irb-common/components/grid/editors/number-text-box-editor.component';
import {MmcImportModelTargetComponent} from '@irb/irb-editors/editor-mmc-model/mmc-import-model-target/mmc-import-model-target.component';
import {DialogComponent} from '@irb-common/components/dialog/dialog.component';
import {NumberTextBoxRendererComponent} from '@irb-common/components/grid/renderers/number-text-box.cell.component';

@Component({ standalone: false,
    selector: 'irb-mmc-securities-model',
    templateUrl: './mmc-securities-model.component.html',
    styleUrls: ['./mmc-securities-model.component.scss']
})
export class MmcSecuritiesModelComponent implements OnInit {

    @Input() data: any;
    @Output() change = new EventEmitter<boolean>();
    @Output() validChange = new EventEmitter<any>();
    @Input() eventModelType: string;

    @ViewChild("dialog") dialog: DialogComponent;

    grid: any;
    gridOptions: any;
    height: string = '100px';
    totalTarget: string = '';
    typedSecurities: string = '';
    custcatNames: any = [];
    isValid: boolean = false;
    recordsMsg: number = 0;
    frameworkComponents: any;
    isUiTest: boolean = false;

    constructor(private editorMmcModelService: EditorMmcModelService,
                private ctas: CalltoactionService,
                private as: AppService) {
    }

    ngOnInit(): void {
        if (this.data && this.data.securityList && this.data.securityList.length !== 0) {
            this.setGridheight();
            this.getTotalTarget();
        }

        this.frameworkComponents = {
            "DeleteCellComponent": DeleteCellComponent,
            "DropdownFilterRendererComponent": DropdownFilterRendererComponent,
            "NumberTextBoxEditorComponent": NumberTextBoxEditorComponent,
            "NumberTextBoxRendererComponent": NumberTextBoxRendererComponent
        };
        this.gridOptions = {
            columnDefs: this.getColumns(),
            floatingFilter: false,
            doesExternalFilterPass: this.doesExternalFilterPass.bind(this),
            isExternalFilterPresent: this.isExternalFilterPresent.bind(this),
        };

        this.editorMmcModelService.getSecurityCustCategorySettings().subscribe((custCats: any) => {
            if (custCats && custCats.items) {
                let ccItem: any;
                for (const record of custCats.items) {
                    ccItem = {
                        name: record.custCatDesc,
                        value: record.custCatId
                    };
                    this.custcatNames.push(ccItem);
                }
            }
        });

        this.editorMmcModelService.importTargets.subscribe((newRecs: any) => {
            this.addImportedRecord(newRecs);
        });

        this.editorMmcModelService.dataSaved.subscribe((newData: any) => {
            this.data.securityList = newData.securityList;
            if (this.grid && this.grid.api) {
                this.grid.api.setGridOption('rowData', this.data.securityList);
            }
        });
    }

    isExternalFilterPresent() {
        return true;
    }

    doesExternalFilterPass(node: any) {
        return !node.data.isUIdeleted;
    }

    onGridReady(params: any) {
        this.grid = params;
        this.grid.api.setGridOption('rowData', this.data.securityList);

        if (this.height && this.data.securityList && this.data.securityList.length !== 0) {
            const h: any = this.height.replace('px', '');

            const ht = parseInt(h);
            if (ht < 300)
                this.grid.api.setGridOption('domLayout', 'autoHeight');
            else
                this.grid.api.setGridOption('domLayout', 'normal');
        }
    }

    onCellValueChanged() {
    }

    private setGridheight() {
        const secData: any = this.data.securityList.filter((el: any) => {
            return el.isUIdeleted === false;
        }).length;
        let ht: number = secData * 30 + 33;
        ht = ht > 300 ? 300 : ht;
        if (secData === 0) {
            ht = 100;
            if (this.grid && this.grid.api)
                this.grid.api.setGridOption('domLayout', 'normal');
        } else {
            if (this.grid && this.grid.api) {
                if (ht < 300)
                    this.grid.api.setGridOption('domLayout', 'autoHeight');
                else
                    this.grid.api.setGridOption('domLayout', 'normal');
            }
        }
        this.height = ht + 'px';
    }

    private getColumns() {
        const columns: any = [
            {
                headerName: "",
                cellRenderer: "DeleteCellComponent",
                cellRendererParams: {
                    message: "Remove this security from the model?",
                    note: "",
                    proceed: this.delete.bind(this),
                    cancel: this.cancelDelete.bind(this),
                    disableDelete: () => !this.data.isProcessing
                },
                resizable: false,
                minWidth: 25,
                maxWidth: 25,
                width: 25,
                suppressColumnsToolPanel: true,
                lockPosition: true,
                suppressMovable: true
            },
            {
                headerName: 'Symbol',
                field: 'symbol',
                width: 100,
                minWidth: 70,
                suppressMovable: true
            },
            {
                headerName: 'Description',
                field: 'description',
                minWidth: 120,
                suppressMovable: true,
                cellRenderer: 'cellRendererFormatter'
            },
            {
                headerName: 'Category',
                field: 'custCatDesc',
                headerComponentParams: {
                    irbColInfo: this.categoryInfo()
                },
                minWidth: 160,
                suppressMovable: true,
                cellClass: 'dropdownFilterCell',
                cellRenderer: 'DropdownFilterRendererComponent',
                cellRendererParams: (params: any) => {
                    return {
                        defaultValue: {name: params.data.custCatDesc, value: params.data.custCatId},
                        values: this.custcatNames,
                        disabled: this.data.isProcessing,
                        onChange: (params: any, item: IDropdownItem) => {
                            params.data.custCatDesc = item.name;
                            params.data.custCatId = item.value;
                            params.api.redrawRows({rowNodes: [params.node]});
                            setTimeout(() => {
                                this.change.emit(true);
                            });
                        }
                    };
                }
            },
            {
                headerName: 'Target %',
                field: 'target',
                width: 100,
                minWidth: 80,
                headerComponentParams: {
                    irbColInfo: this.targetInfo()
                },
                suppressMovable: true,
                // editable: true,
                cellClass: 'numberTextBoxCell targetPercentClassForFocus',
                // cellEditor: 'NumberTextBoxEditorComponent',
                // cellEditorParams: () => {
                //     return {
                //         onUpdate: this.onApplyTarget.bind(this),
                //         autofocus: true,
                //         autoSelect: true,
                //         min: 0,
                //         max: 100,
                //         places: 1,
                //         addZeroIfDotFirst: true,
                //         invalidMessage: 'Security allocations may not have more than one decimal place.',
                //         invalidRangeMessage: 'This value is out of range',
                //         roundIfInvalid: true
                //     }
                // }
                cellRenderer: "NumberTextBoxRendererComponent",
                cellRendererParams: () => {
                    return {
                        min: 0,
                        max: 100,
                        places: 1,
                        autoSelect: true,
                        autoHeight: true,
                        addZeroIfDotFirst: true,
                        invalidMessage: 'Security allocations may not have more than one decimal place.',
                        disabled: this.data.isProcessing,
                        onChange: (params: any, c: any) => {
                            this.onApplyTarget(params, c);
                        }
                    };
                }
            }
        ];
        return columns;
    }

    private categoryInfo() {
        const msg = 'Select an asset category (style or sector) from the drop down list for each security. This will help advisors better understand the model\'s exposure to the market. If you do not see an asset category that represents the security, please contact your Relationship Manager to have one added.';
        return msg;
    }

    private targetInfo() {
        const msg = 'The targets for your model must total 100%. Advisors cannot see the targets for securities until they have accepted the terms and conditions and subscribed to the model.';
        return msg;
    }

    private updateGridData() {
        if (this.grid && this.grid.api) {
            this.grid.api.setGridOption('rowData', this.data.securityList);
        }
        this.setGridheight();
        this.getTotalTarget();
        this.change.emit(true);
    }

    onApplyTarget(params: any, c: any) {
        if (params.data.target !== c.value && c.isValid) {
            params.data.target = parseFloat(c.value);
        }
        params.api.redrawRows({rowNodes: [params.node]});

        this.getTotalTarget();
        this.change.emit(true);
    }

    private checkAllTargets() {
        let isValid = true;
        const existsSec = this.data.securityList.filter((el: any) => {
            return !el.target;
        });
        isValid = existsSec && existsSec.length === 0 ? true : false;
        if (!isValid) {
            this.validChange.emit({type: 'securities', valid: false});
        }
    }

    delete(deletedEl: any) {
        let pos: number = null;
        for (let i = 0; i < this.data.securityList.length; i++) {
            if (this.data.securityList[i].symbol === deletedEl.symbol) {
                if (this.data.securityList[i].isNew) {
                    pos = i;
                }
                this.data.securityList[i].isUIdeleted = true;
                break;
            }
        }
        if (pos !== null) {
            this.data.securityList.splice(pos, 1);
        }
        this.updateGridData();
    }

    cancelDelete() {
        this.ctas.remove();
    }

    getTotalTarget() {
        let total: number = 0;
        this.totalTarget = '';
        const secData: any = this.data.securityList.filter((el: any) => {
            return el.isUIdeleted === false;
        });
        if (secData && secData.length !== 0) {
            this.recordsMsg = secData.length;
            secData.forEach((item: any) => {
                total += item.target
            });
            total = Number(total.toFixed(1));
            this.totalTarget = total === 0 ? '0.0' : total.toFixed(1);
        } else {
            this.recordsMsg = 0;
        }
        this.isValid = total === 100 ? true : false;
        this.validChange.emit({type: 'securities', valid: this.isValid});
        this.checkAllTargets();
    }

    addSecuritiesFromMaster() {
        if (this.typedSecurities !== '') {
            let reqParamSec: Array<string> = [], duplicates: Array<string> = [];
            const listOfTyped: Array<string> = this.typedSecurities.toUpperCase().split(' ');
            if (Array.isArray(listOfTyped) && listOfTyped.length !== 0) {
                listOfTyped.forEach((sec: string) => {
                    if (this.checkForDuplicates(sec)) {
                        duplicates.push(sec);
                    } else {
                        reqParamSec.push(sec)
                    }
                });

                if (reqParamSec.length !== 0) {
                    this.editorMmcModelService.addSecurityFromMaster(reqParamSec).subscribe((res: any) => {
                        this.handleSecFromMasterResponse(res);
                    });
                }
                if (duplicates.length !== 0) {
                    this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                        severity: "warn",
                        summary: "",
                        detail: "The following securities are already part of the model: " + duplicates.join(',')
                    });
                }
            }
            this.typedSecurities = '';
        }
    }

    private checkForDuplicates(sec: string) {
        const existsSec = this.data.securityList.filter((el: any) => {
            return el.symbol === sec && !el.isUIdeleted;
        });
        return existsSec && existsSec.length !== 0;
    }

    private handleSecFromMasterResponse(res: any) {
        if (res.responseData && Array.isArray(res.responseData)) {
            if (typeof res.responseData[1] != "undefined" && res.responseData[1].length > 0) {
                let d = [];
                for (var i = 0; i < res.responseData[1].length; i++) {
                    d.push(res.responseData[1][i].symbol);
                }
                this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                    severity: "warn",
                    summary: "",
                    detail: "The following securities are not recognized and were not added to the model: " + d.join(", ")
                });
            }
            if (res.responseData[0] && res.responseData[0].length !== 0) {
                let dataCopy = JSON.parse(JSON.stringify(this.data.securityList));

                res.responseData[0].forEach((newSec: any) => {
                    if (newSec.custCatId === null) {
                        newSec.custCatDesc = "Uncategorized Custom Category";
                        newSec.custCatId = 0;
                        newSec.custCatName = "Uncategorized";
                    }
                    newSec.target = newSec.target ? newSec.target : 0;
                    newSec.isNew = true;
                    newSec.isUIdeleted = false;
                    dataCopy.push(newSec);
                });

                this.data.securityList = dataCopy;
                this.updateGridData();

                this.openEditor();
            }
        }
    }

    private openEditor() {
        let rowIdx: number = null;
        if (this.grid && this.grid.api) {
            this.grid.api.forEachNode((node: any) => {
                if (!node.data.target && rowIdx === null) {
                    rowIdx = node.rowIndex;
                }
            });

            if (rowIdx !== null) {
                // this.params.api.startEditingCell({
                //     rowIndex: rowIdx,
                //     colKey: 'target'
                // });

                setTimeout(() => {
                    this.grid.api.setFocusedCell(rowIdx, 'target');
                    let elem: any = document.querySelectorAll("#mmc-securities-model-grid .ag-row-focus .targetPercentClassForFocus input");
                    if (elem && elem.length !== 0) {
                        elem[0].focus();
                    }
                }, 100);
                this.validChange.emit({type: 'securities', valid: false});
            }
        }
    }

    importModelTarget() {
        const config = this.dialog.getDefaultConfig();
        config.minHeight = "100px";
        config.height = "auto";
        config.width = "550px";
        this.dialog.open(MmcImportModelTargetComponent, config);
    }

    addImportedRecord(newRecs: any) {
        let newSecsList: any = [];

        newRecs.forEach((newSec: any) => {
            if (newSec.custCatId === null) {
                newSec.custCatDesc = "Uncategorized Custom Category";
                newSec.custCatId = 0;
                newSec.custCatName = "Uncategorized";
            }
        });

        if (this.data.securityList.length === 0) {
            this.data.securityList = [].concat(newRecs);
        } else {
            let isFound: boolean = false;
            newRecs.forEach((newSec: any) => {
                isFound = false;
                this.data.securityList.forEach((sec: any) => {
                    if (sec.symbol === newSec.symbol && !sec.isUIdeleted) {
                        sec.isUIdeleted = false;
                        sec.target = newSec.target;
                        isFound = true;
                    }
                });
                if (!isFound) {
                    newSec.isNew = true;
                    newSecsList.push(newSec);
                }
            });
            if (newSecsList.length !== 0) {
                this.data.securityList = this.data.securityList.concat(newSecsList);
            }
            let isStillExist: boolean = false;
            this.data.securityList.forEach((sec: any) => {
                isStillExist = false;
                newRecs.forEach((newSec: any) => {
                    if (sec.symbol === newSec.symbol) {
                        isStillExist = true;
                    }
                });
                if (!isStillExist) {
                    sec.isUIdeleted = true;
                }
            });

        }
        if (this.grid && this.grid.api) {
            this.grid.api.setGridOption('rowData', this.data.securityList);
            this.updateGridData();
            this.openEditor();
        }
    }

}
